// SPIKE (MYN-104) — a corpus kept on the device rather than in the build.
//
// Today the pair is chosen when the app is BUILT: data/pairs/<id>/ is copied into the bundle and
// that is what the app teaches, for ever. Kennard wants it chosen in the app — pick what you are
// learning and what you already speak, download that pair, and replace it when you change your
// mind, holding one at a time rather than all of them.
//
// This file exists to answer one question: can the app read its corpus from device storage instead
// of from the bundle? The rest of the design — the picker, the catalogue of available pairs, the
// progress model that survives a swap — is the epic, not the spike.
//
// It reuses what the audio download already proved (MYN-94): the origin private file system is
// real files in the app's own container, it survives restarts, and it is the right home for
// anything too big or too changeable to bundle.

const DIR = 'corpus';

/** The files that make a pair. `strokes` is optional; the rest are not. */
export const CORPUS_FILES = [
  'pair.json',
  'vocab.json',
  'sentences.json',
  'stories.json',
  'tutor.json',
  'hints.json',
] as const;

export const OPTIONAL_FILES = ['strokes.json'] as const;

function supported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.storage?.getDirectory;
}

async function dir(create: boolean): Promise<FileSystemDirectoryHandle | null> {
  if (!supported()) return null;
  try {
    return await (await navigator.storage.getDirectory()).getDirectoryHandle(DIR, { create });
  } catch {
    return null;
  }
}

/** Which pair is installed on this device, or null when the app should use the one it shipped with. */
export async function installed(): Promise<string | null> {
  const base = await dir(false);
  if (!base) return null;
  try {
    const handle = await base.getFileHandle('pair.json');
    const pair = JSON.parse(await (await handle.getFile()).text()) as {
      learning?: { code?: string };
      explainedIn?: { code?: string };
    };
    const learning = pair.learning?.code;
    const explained = pair.explainedIn?.code;
    return learning && explained ? `${learning}-${explained}` : null;
  } catch {
    return null;
  }
}

/** One file of the installed corpus, or null when there is none. */
export async function read(name: string): Promise<string | null> {
  const base = await dir(false);
  if (!base) return null;
  try {
    return await (await (await base.getFileHandle(name)).getFile()).text();
  } catch {
    return null;
  }
}

/**
 * Put a pair on the device, replacing whatever was there.
 *
 * ONE AT A TIME, which is the whole point: the previous corpus is removed first rather than kept
 * beside the new one. A learner who goes back changes their mind, not their storage plan, and the
 * text is small enough (1.5 MB gzipped for the whole Mandarin corpus) that fetching it again is
 * cheaper than carrying every pair they ever tried.
 */
export async function install(files: Record<string, string>): Promise<void> {
  await remove();
  const base = await dir(true);
  if (!base) throw new Error('no device storage for a corpus');
  for (const [name, text] of Object.entries(files)) {
    const handle = await base.getFileHandle(name, { create: true });
    const w = await handle.createWritable();
    await w.write(text);
    await w.close();
  }
}

/** Throw the installed corpus away and fall back to the one in the build. */
export async function remove(): Promise<void> {
  if (!supported()) return;
  const opfs = await navigator.storage.getDirectory().catch(() => null);
  if (opfs) await opfs.removeEntry(DIR, { recursive: true }).catch(() => undefined);
}
